import { Timestamp } from '@firebase/firestore';
import { HOUR24_DATE_CONFIG } from '../global';

/**
 * Holds information about patient vitals. Vitals can be empty string values.
 */
export class Vital {
  /**
   * The date that the vital was created. Also used as the primary for the vital.
   */
  date: Timestamp;

  /**
   * Heart rate in beats per minute (bpm).
   */
  heartRate: number | '';

  /**
   * Oxygen saturation in percentage (0-100%).
   */
  oxySat: number | '';

  oxygen: string;

  oxygenInput: string;

  /**
   * The number of respirations per minute.
   */
  respirations: number | '';

  /**
   * Temperature in degrees Celcius.
   */
  temp: number | '';

  /**
   * Systolic blood pressure.
   */
  systolic: number | '';

  /**
   * Diastolic blood pressure
   */
  diastolic: number | '';

  /**
   * Flag indicating whether the vital was deleted by the user or not.
   */
  deleted: boolean;

  constructor(
    date?: Timestamp,
    heartRate?: number | '',
    oxySat?: number | '',
    oxygen?: string,
    oxygenInput?: string,
    respirations?: number | '',
    temp?: number | '',
    systolic?: number | '',
    diastolic?: number | '',
    deleted?: boolean
  ) {
    this.date = date ? date : Timestamp.now();
    this.heartRate = heartRate ? heartRate : '';
    this.oxySat = oxySat ? oxySat : '';
    this.oxygen = oxygen ? oxygen : '';
    this.oxygenInput = oxygenInput ? oxygenInput : '';
    this.respirations = respirations ? respirations : '';
    this.temp = temp ? temp : '';
    this.systolic = systolic ? systolic : '';
    this.diastolic = diastolic ? diastolic : '';
    this.deleted = deleted || false;
  }

  /**
   * Prepares the Vital's format for Firebase by converting the vital to a JSON object,
   * with the JSON object's date being a Timestamp.
   *
   * @returns a Vital represented as JSON.
   */
  toFirebase() {
    const obj = JSON.parse(JSON.stringify(Object.assign({}, this)));
    obj.date = Timestamp.fromMillis(obj.date.seconds * 1000);

    return obj;
  }

  /**
   * Used to display different color for heart rates not in normal range.
   * Normal range: 40-100 bpm.
   *
   * @returns whether the heart rate is within a normal range.
   */
  get isHeartRateWithinNormalRange(): boolean {
    return this.heartRate === '' || this.heartRate >= 40 && this.heartRate <= 100;
  }

  /**
   * Used to display different color for oxygen saturations not in normal range.
   * Normal range: 92-100%.
   *
   * @returns whether the oxygen saturation is within a normal range.
   */
  get isOxySatWithinNormalRange(): boolean {
    return this.oxySat === '' || this.oxySat >= 92 && this.oxySat <= 100;
  }

  get oxygenInputUnit(): string {
    if (this.oxygen === 'Nasal Cannula') {
      return 'Liters';
    } else if (this.oxygen === 'Venturi Mask') {
      return '%';
    }
    return '';
  }

  /**
   * Used to display different color for respirations not in normal range.
   * Normal range: 12-25 respirations.
   *
   * @returns whether the number of respirations is within a normal range.
   */
  get isRespirationsWithinNormalRange(): boolean {
    return this.respirations === '' || this.respirations >= 12 && this.respirations <= 25;
  }

  /**
   * Used to display different color for temperatures not in normal range.
   * Normal range: 36.1-37.2 °C.
   *
   * @returns whether the temperature is within a normal range.
   */
  get isTemperatureWithinNormalRange(): boolean {
    return this.temp === '' || this.temp >= 36.1 && this.temp <= 37.9;
  }

  /**
   * Used to display different color for systolic blood pressures not in normal range.
   * Normal range: below 180.
   *
   * @returns whether the systolic blood pressure is within a normal range.
   */
  get isSystolicWithinNormalRange(): boolean {
    return this.systolic === '' || this.systolic < 180;
  }

  /**
   * Used to display different color for diastolic blood pressures not in normal range.
   * Normal range: 60-100.
   *
   * @returns whether the diastolic blood pressure is within a normal range.
   */
  get isDiastolicWithinNormalRange(): boolean {
    return this.diastolic === '' || this.diastolic >= 60 && this.diastolic <= 100;
  }

  toggleDelete(): void {
    this.deleted = !this.deleted;
  }

  /**
   * @returns A date as string, formatted as 'DD/MM/YYYY @ HH:MM' (24-hour time).
   */
  get dateString() {
    return this.date.toDate().toLocaleString([], HOUR24_DATE_CONFIG).replace(',', ' @');
  }
}
